import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AdminStatusPill from '../../../../components/admin/AdminStatusPill';
import type { StoreDetailsFeatureFlagsTableProps } from '../../../../types/features/storeDetails/featureFlags';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminTableSx,
} from '../../../../styles/adminComponentStyles';
import {
  getStoreDetailsFeatureFlagAssignmentDisabled,
  getStoreDetailsFeatureFlagEffectiveStatus,
  getStoreDetailsFeatureFlagGlobalStatus,
  getStoreDetailsFeatureFlagRolloutStatus,
  STORE_DETAILS_FEATURE_FLAG_REASON_LABELS,
} from '../utils/storeDetailsFeatureFlagsDisplay';

const StoreDetailsFeatureFlagsTable: React.FC<StoreDetailsFeatureFlagsTableProps> = ({
  error,
  loading,
  mutatingFeatureId,
  mutationError,
  onAssignmentChange,
  onRetry,
  rows,
}) => (
  <TableContainer sx={{ overflowX: 'auto' }}>
    <Table
      size="small"
      sx={adminCombineSx(adminTableSx, {
        minWidth: 980,
        tableLayout: 'fixed',
      })}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '28%' }}>Feature</TableCell>
          <TableCell sx={{ width: '17%' }}>Assignment</TableCell>
          <TableCell sx={{ width: '14%' }}>Effective Status</TableCell>
          <TableCell sx={{ width: '23%' }}>Reason</TableCell>
          <TableCell sx={{ width: '8%' }}>Global</TableCell>
          <TableCell sx={{ width: '10%' }}>Rollout</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading && (
          <TableRow>
            <TableCell colSpan={6} sx={{ borderBottom: 0, py: 6, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </TableCell>
          </TableRow>
        )}

        {!loading && error && (
          <TableRow>
            <TableCell colSpan={6} sx={{ borderBottom: 0, py: 5 }}>
              <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.danger }}>
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onRetry}
                  sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
                >
                  Retry
                </Button>
              </Stack>
            </TableCell>
          </TableRow>
        )}

        {!loading && !error && rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} sx={{ borderBottom: 0, py: 6, textAlign: 'center' }}>
              <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                No feature flags found
              </Typography>
            </TableCell>
          </TableRow>
        )}

        {!loading && !error && mutationError && (
          <TableRow>
            <TableCell colSpan={6} sx={{ py: 1.5 }}>
              <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.danger }}>
                {mutationError}
              </Typography>
            </TableCell>
          </TableRow>
        )}

        {!loading && !error && rows.map((row) => {
          const assignmentDisabled = getStoreDetailsFeatureFlagAssignmentDisabled(row);
          const effectiveStatus = getStoreDetailsFeatureFlagEffectiveStatus(row.effectiveEnabled);
          const globalStatus = getStoreDetailsFeatureFlagGlobalStatus(row.globalEnabled);
          const rolloutStatus = getStoreDetailsFeatureFlagRolloutStatus(row.rolloutScope, row.rolloutTarget);

          return (
            <TableRow key={row.id} hover>
              <TableCell>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                  {row.displayName}
                </Typography>
                <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                  {row.featureKey}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                  <Switch
                    checked={row.assignedToStore}
                    disabled={assignmentDisabled || Boolean(mutatingFeatureId)}
                    inputProps={{ 'aria-label': `${row.displayName} assignment` }}
                    onChange={(event) => onAssignmentChange(row.id, event.target.checked)}
                    size="small"
                  />
                </Box>
              </TableCell>
              <TableCell>
                <AdminStatusPill label={effectiveStatus.label} tone={effectiveStatus.tone} />
              </TableCell>
              <TableCell>
                <Typography sx={{ ...adminTypography.body, color: adminColors.text }}>
                  {row.reason ? STORE_DETAILS_FEATURE_FLAG_REASON_LABELS[row.reason] : ''}
                </Typography>
              </TableCell>
              <TableCell>
                <AdminStatusPill label={globalStatus.label} tone={globalStatus.tone} />
              </TableCell>
              <TableCell>
                <AdminStatusPill label={rolloutStatus.label} tone={rolloutStatus.tone} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

export default React.memo(StoreDetailsFeatureFlagsTable);
